import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    FormControlLabel,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfoHint from './InfoHint';
import useRequest from '../../hooks/useRequest';
import type { SalaryRole } from '../../domain/salary/types';

interface Props {
    roles: SalaryRole[];
    reload: () => Promise<void>;
}

const RolesTab = ({ roles, reload }: Props) => {
    const { t } = useTranslation();
    const request = useRequest();
    const [newRoleName, setNewRoleName] = useState('');

    const handleCreate = async () => {
        if (!newRoleName) return;
        await request({
            path: '/salary/roles',
            method: 'POST',
            params: { name: newRoleName, hasShiftSystem: true },
        });
        setNewRoleName('');
        await reload();
    };

    const handleSaveConfig = async (
        role: SalaryRole,
        patch: Partial<NonNullable<SalaryRole['config']>>,
    ) => {
        await request({
            path: `/salary/roles/${role.id}/config`,
            method: 'PUT',
            params: patch,
        });
        await reload();
    };

    return (
        <Stack spacing={2}>
            <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                    {t('salary.roles.addTitle')}
                    <InfoHint title={t('salary.info.roles.section')} />
                </Typography>
                <Stack direction="row" spacing={1}>
                    <TextField
                        size="small"
                        label={t('salary.roles.name')}
                        value={newRoleName}
                        onChange={e => setNewRoleName(e.target.value)}
                    />
                    <Button variant="contained" onClick={handleCreate}>
                        {t('common.add')}
                    </Button>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t('salary.roles.pageAccessNote')}
                </Typography>
            </Paper>

            {roles.map(role => (
                <Accordion key={role.id}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ fontWeight: 600 }}>{role.name}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <RoleConfigForm role={role} onSave={handleSaveConfig} />
                    </AccordionDetails>
                </Accordion>
            ))}
        </Stack>
    );
};

interface RoleConfigFormProps {
    role: SalaryRole;
    onSave: (role: SalaryRole, patch: Partial<NonNullable<SalaryRole['config']>>) => Promise<void>;
}

const RoleConfigForm = ({ role, onSave }: RoleConfigFormProps) => {
    const { t } = useTranslation();
    const config = role.config;
    const [weekendMultiplier, setWeekendMultiplier] = useState(
        String(config?.weekendMultiplier ?? 2),
    );
    const [holidayMultiplier, setHolidayMultiplier] = useState(
        String(config?.holidayMultiplier ?? 2),
    );
    const [nightFixedHours, setNightFixedHours] = useState(String(config?.nightFixedHours ?? 8));
    const [autoRaisePercent, setAutoRaisePercent] = useState(String(config?.autoRaisePercent ?? 0));
    const [autoRaiseDates, setAutoRaiseDates] = useState((config?.autoRaiseDates ?? []).join(','));
    const [taxRate, setTaxRate] = useState(String(config?.taxRate ?? 0));
    const [monthlyNormHours, setMonthlyNormHours] = useState(
        String(config?.monthlyNormHours ?? 168),
    );
    const [isFixed, setIsFixed] = useState(role.isFixedSalary);

    const handleSave = async () => {
        await onSave(role, {
            weekendMultiplier: Number(weekendMultiplier),
            holidayMultiplier: Number(holidayMultiplier),
            nightFixedHours: Number(nightFixedHours),
            autoRaisePercent: Number(autoRaisePercent),
            autoRaiseDates: autoRaiseDates
                .split(',')
                .map(s => s.trim())
                .filter(Boolean),
            taxRate: Number(taxRate),
            monthlyNormHours: Number(monthlyNormHours),
        });
    };

    return (
        <Stack spacing={2}>
            <Stack direction="row" alignItems="center">
                <FormControlLabel
                    control={
                        <Switch checked={isFixed} onChange={e => setIsFixed(e.target.checked)} />
                    }
                    label={t('salary.roles.fixedSalary')}
                />
                <InfoHint title={t('salary.info.roles.fixedSalary')} />
            </Stack>
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={1}
                flexWrap="wrap"
                alignItems="center"
            >
                <TextField
                    size="small"
                    label={t('salary.roles.weekendMultiplier')}
                    value={weekendMultiplier}
                    onChange={e => setWeekendMultiplier(e.target.value)}
                />
                <InfoHint title={t('salary.info.roles.weekendMultiplier')} />
                <TextField
                    size="small"
                    label={t('salary.roles.holidayMultiplier')}
                    value={holidayMultiplier}
                    onChange={e => setHolidayMultiplier(e.target.value)}
                />
                <InfoHint title={t('salary.info.roles.holidayMultiplier')} />
                <TextField
                    size="small"
                    label={t('salary.roles.nightFixedHours')}
                    value={nightFixedHours}
                    onChange={e => setNightFixedHours(e.target.value)}
                />
                <InfoHint title={t('salary.info.roles.nightFixedHours')} />
                <TextField
                    size="small"
                    label={t('salary.roles.autoRaisePercent')}
                    value={autoRaisePercent}
                    onChange={e => setAutoRaisePercent(e.target.value)}
                />
                <InfoHint title={t('salary.info.roles.autoRaise')} />
                <TextField
                    size="small"
                    label={t('salary.roles.autoRaiseDates')}
                    value={autoRaiseDates}
                    onChange={e => setAutoRaiseDates(e.target.value)}
                    placeholder="01-15,07-15"
                    helperText={t('salary.roles.autoRaiseDatesHelp')}
                />
                <TextField
                    size="small"
                    label={t('salary.roles.taxRate')}
                    value={taxRate}
                    onChange={e => setTaxRate(e.target.value)}
                    helperText={t('salary.roles.taxRateHelp')}
                />
                <InfoHint title={t('salary.info.roles.taxRate')} />
                <TextField
                    size="small"
                    label={t('salary.roles.monthlyNormHours')}
                    value={monthlyNormHours}
                    onChange={e => setMonthlyNormHours(e.target.value)}
                    helperText={t('salary.roles.monthlyNormHoursHelp')}
                />
                <InfoHint title={t('salary.info.roles.monthlyNormHours')} />
            </Stack>
            <Stack direction="row" justifyContent="flex-end">
                <Button variant="contained" onClick={handleSave}>
                    {t('common.save')}
                </Button>
            </Stack>
        </Stack>
    );
};

export default RolesTab;
